import { defineDocument } from '../../schema';
export const blogBadBunniesBurstOutOfTheBurrowDocument = defineDocument({
    id: 'blog:bad-bunnies-burst-out-of-the-burrow',
    kind: 'blog',
    slug: 'bad-bunnies-burst-out-of-the-burrow',
    route: '/blog/bad-bunnies-burst-out-of-the-burrow',
    seo: {
        title: 'Bad Bunnies Burst Out of the Burrow',
        description: 'New co-op strategy game hops into our library',
        imageAsset: 'coverBadBunnies',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Airhead Blog',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Bad Bunnies Burst Out of the Burrow',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'New co-op strategy game hops into our library',
            },
        ],
        imageAsset: 'coverBadBunnies',
        imageAlt: 'Bad Bunnies cover art',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-03-25T00:00:00.000Z'),
        updatedAt: new Date('2026-03-25T00:00:00.000Z'),
        tags: ['games'],
    },
    sections: [
        {
            id: 'article',
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Spring is here, Bad Bunny performed at the Super Bowl, and we have been playing a lot of Pandemic lately. Coincidence? Maybe. But somewhere in that perfect storm, ',
                        },
                        {
                            type: 'link',
                            href: '/discover/games/bad-bunnies',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Bad Bunnies',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: " came to life: a cooperative strategy game, where it's you against the board. No individual scores, no eliminations. Just one shared enemy: a bunny population that will not stop multiplying.",
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'article__a-rabbit-problem-inspired-by-a-plague',
                    title: [
                        {
                            type: 'text',
                            text: 'A Rabbit Problem Inspired by a Plague',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "If you have played Pandemic, the concept will feel familiar: a map that gets worse every round, a team making tough calls under pressure, and no single strategy that works every time. We wanted to capture that same tension but wrap it in something lighter without losing the strategic depth. Swap diseases for bunnies, cities for burrows, sprinkle in a little ridiculousness, and you've got Bad Bunnies.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The map starts with 10 bunnies and 16 burrows connected by tunnels. Your group votes on an action card and a target, with the winning combination applied to the map. Then, the bunnies reproduce: for every two bunnies in a burrow, one new bunny appears in each neighboring burrow. The spread is fast, and it compounds quickly.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Wipe every bunny out to achieve victory. Let the population hit 1,000 and the bunnies win.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__eight-ways-to-fight-back',
                    title: [
                        {
                            type: 'text',
                            text: 'Eight Ways to Fight Back',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Creating and calibrating the action cards was the most interesting part of building Bad Bunnies. We wanted cards that felt meaningfully different from each other, with real strategic tradeoffs.',
                                },
                            ],
                        },
                        {
                            type: 'quote',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The biggest challenge was finding the right balance between action power and bunny reproduction. It had to be a fair fight for both the players and the bunnies.',
                                },
                            ],
                            attribution: [
                                {
                                    type: 'text',
                                    text: 'Jay, Developer',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Some cards are aggressive and permanent: ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Destroy Tunnel',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' and ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Destroy Burrow',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' reshape the map for good, and ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Give Carrot',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' shuts down reproduction in a burrow forever. Other cards are crowd-control tools that thin the population such as ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Poison Colony',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Cull Colony',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', and ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Add Fox',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '. There are also temporary measures, like ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Block Burrow',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' and ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Give Lettuce',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', to buy time while you figure out your next move or try to land a powerful permanent action card.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__behind-the-burrow',
                    title: [
                        {
                            type: 'text',
                            text: 'Behind the Burrow',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "Most strategy games ask for 45 minutes or more. We set out to build one that fits into the same quick, pick-up-and-play format as our other party games without sacrificing the depth. Bad Bunnies plays in 10 to 20 minutes because the urgency is built in from the first turn: bunnies reproduce fast, tunnels spread them across the map, and the compounding math means things can spiral out of control quickly. While it's possible to get a lucky hand and make quick work of the bunnies, it's more likely the population starts running away and your group has to scramble to contain it.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "We also spent a lot of time making this work as both a solo puzzle and an interesting multiplayer game. Playing alone, it's a quiet strategy exercise where you control every decision. With a full group, it can turn into a loud negotiation about which action to take and where. Either way, Bad Bunnies is always a fun challenge that players should not expect to win every time. The board state can evolve in wildly different directions and, depending on how players focus their attention, every game will play out differently.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The best moments of developing any game are the ones that make us laugh: watching hundreds of angel bunnies stream up towards heaven, creating sound effects like ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'What the fox?!',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ', and bumping tunes such as ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Like Rabbits',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' by Irie Pop Collective. We hope players delight in these little details as much as we do.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__something-new-for-the-platform',
                    title: [
                        {
                            type: 'text',
                            text: 'Something New for the Platform',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Bad Bunnies is the first game we built with a dynamic board that changes as you play. That kind of layered, evolving game state was new territory for us, and it sets the stage for more puzzle and strategy games in the future.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__hop-in',
                    title: [
                        {
                            type: 'text',
                            text: 'Hop In',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The bunnies are not going to stop themselves. ',
                                },
                                {
                                    type: 'link',
                                    href: '/library',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Head to the game library',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' before they take over the world.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        },
    ],
});
//# sourceMappingURL=bad-bunnies-burst-out-of-the-burrow.js.map